// Pure sender-side selective-repeat ARQ state machine for LQP1 QR packets.
// The caller owns display timing and turns POLL descriptors into optical QR
// packets. This module only consumes monotonic timestamps and validated STATUS
// tokens, so every transition is deterministic and directly testable.

import { crc32c } from './crc32c.js';
import { parseQrPacket } from '../optical/qr-stream.js';

export const QR_ARQ_STATE = Object.freeze({
  SEND_PENDING: 'SEND_PENDING',
  POLL: 'POLL',
  WAIT_STATUS: 'WAIT_STATUS',
  SEND_MISSING: 'SEND_MISSING',
  COMPLETE: 'COMPLETE',
  FAILED: 'FAILED',
});

export class QrArqSender {
  constructor(transfer, {
    statusTimeoutMs = 2500,
    maxRounds = 4,
    maxPollAttempts = 3,
    roundIdFactory = defaultRoundId,
  } = {}) {
    validateTransfer(transfer);
    if (!Number.isFinite(statusTimeoutMs) || statusTimeoutMs <= 0) {
      throw new RangeError('statusTimeoutMs doit être strictement positif');
    }
    if (!Number.isInteger(maxRounds) || maxRounds < 1 || maxRounds > 255) {
      throw new RangeError('maxRounds doit être compris entre 1 et 255');
    }
    if (!Number.isInteger(maxPollAttempts) || maxPollAttempts < 1 || maxPollAttempts > 255) {
      throw new RangeError('maxPollAttempts doit être compris entre 1 et 255');
    }
    if (typeof roundIdFactory !== 'function') throw new TypeError('roundIdFactory doit être une fonction');

    this.transfer = transfer;
    this.statusTimeoutMs = statusTimeoutMs;
    this.maxRounds = maxRounds;
    this.maxPollAttempts = maxPollAttempts;
    this.roundIdFactory = roundIdFactory;
    this.state = QR_ARQ_STATE.SEND_PENDING;
    this.round = 1;
    this.acknowledged = new Set();
    this.queue = allIndices(transfer.total);
    this.cursor = 0;
    this.sentThisRound = new Set();
    this.currentPoll = null;
    this.pollAttempts = 0;
    this.deadlineMs = null;
    this.lastNowMs = null;
    this.failureCode = null;
  }

  /**
   * Return the next action at `nowMs`. DATA indices are yielded at most once in
   * the current round. A timeout retries the same POLL; only a validated STATUS
   * can start a bounded missing-only round.
   */
  nextAction(nowMs) {
    this.observeTime(nowMs);
    if (this.state === QR_ARQ_STATE.COMPLETE) return { type: 'COMPLETE', ...this.snapshot() };
    if (this.state === QR_ARQ_STATE.FAILED) return { type: 'FAILED', ...this.snapshot() };

    if (this.state === QR_ARQ_STATE.WAIT_STATUS) {
      if (nowMs < this.deadlineMs) {
        return { type: 'WAIT_STATUS', deadlineMs: this.deadlineMs, ...this.snapshot() };
      }
      this.onTimeout();
      if (this.state === QR_ARQ_STATE.FAILED) return { type: 'FAILED', ...this.snapshot() };
    }

    if (this.state === QR_ARQ_STATE.SEND_PENDING || this.state === QR_ARQ_STATE.SEND_MISSING) {
      if (this.cursor < this.queue.length) {
        const index = this.queue[this.cursor++];
        if (this.sentThisRound.has(index)) return this.fail('QR_ARQ_INTERNAL_DUPLICATE');
        this.sentThisRound.add(index);
        if (this.cursor === this.queue.length) this.state = QR_ARQ_STATE.POLL;
        return {
          type: 'DATA',
          packet: this.transfer.packets[index],
          index,
          round: this.round,
          ...this.snapshot(),
        };
      }
      this.state = QR_ARQ_STATE.POLL;
    }

    if (this.state === QR_ARQ_STATE.POLL) {
      if (!this.currentPoll) {
        const roundId = this.roundIdFactory({
          sessionId: this.transfer.sessionId,
          transferCrc: this.transfer.transferCrc,
          total: this.transfer.total,
          round: this.round,
        });
        assertHex32(roundId, 'roundId');
        this.currentPoll = {
          sessionId: this.transfer.sessionId,
          transferCrc: this.transfer.transferCrc,
          total: this.transfer.total,
          roundId,
          round: this.round,
        };
      }
      this.pollAttempts += 1;
      this.deadlineMs = nowMs + this.statusTimeoutMs;
      this.state = QR_ARQ_STATE.WAIT_STATUS;
      return {
        type: 'POLL',
        poll: { ...this.currentPoll },
        pollAttempt: this.pollAttempts,
        retry: this.pollAttempts > 1,
        deadlineMs: this.deadlineMs,
        ...this.snapshot(),
      };
    }

    return this.fail('QR_ARQ_INVALID_STATE');
  }

  /** Accept a STATUS only for the active session, transfer and POLL nonce. */
  receiveStatus(inputStatus, nowMs) {
    this.observeTime(nowMs);
    if (this.state !== QR_ARQ_STATE.WAIT_STATUS) {
      return { accepted: false, ignored: true, code: 'QR_ARQ_NOT_WAITING', ...this.snapshot() };
    }
    if (nowMs >= this.deadlineMs) {
      this.onTimeout();
      return { accepted: false, ignored: true, code: 'QR_ARQ_STATUS_TIMEOUT', ...this.snapshot() };
    }

    const status = normalizeStatus(inputStatus);
    if (!status.ok || status.type !== 'STATUS') {
      return { accepted: false, ignored: true, code: status.code ?? 'QR_ARQ_BAD_STATUS', ...this.snapshot() };
    }
    if (!this.matchesCurrentPoll(status)) {
      return { accepted: false, ignored: true, code: 'QR_ARQ_STALE_STATUS', ...this.snapshot() };
    }

    // STATUS is cumulative. If the receiver forgot an acknowledged DATA (for
    // example after a camera restart), missing-only rounds cannot repair it
    // without breaking the guarantee that acknowledged DATA are never resent.
    const received = new Set(status.received);
    if ([...this.acknowledged].some((index) => !received.has(index))) {
      this.fail('QR_ARQ_RECEIVER_STATE_LOST');
      return { accepted: true, ignored: false, complete: false, ...this.snapshot() };
    }

    for (const index of status.received) this.acknowledged.add(index);
    const pending = allIndices(this.transfer.total).filter((index) => !this.acknowledged.has(index));
    const bitmapComplete = status.received.length === this.transfer.total && status.missing.length === 0;
    if (bitmapComplete) {
      this.state = QR_ARQ_STATE.COMPLETE;
      this.queue = [];
      this.cursor = 0;
      this.deadlineMs = null;
      return { accepted: true, ignored: false, complete: true, ...this.snapshot() };
    }

    if (!this.startNextRound(pending, 'QR_ARQ_MAX_ROUNDS_STATUS')) {
      return { accepted: true, ignored: false, complete: false, ...this.snapshot() };
    }
    return { accepted: true, ignored: false, complete: false, ...this.snapshot() };
  }

  snapshot() {
    const acknowledged = [...this.acknowledged].sort((a, b) => a - b);
    return {
      state: this.state,
      round: this.round,
      maxRounds: this.maxRounds,
      maxPollAttempts: this.maxPollAttempts,
      pollAttempts: this.pollAttempts,
      sessionId: this.transfer.sessionId,
      transferCrc: this.transfer.transferCrc,
      total: this.transfer.total,
      acknowledged,
      pending: allIndices(this.transfer.total).filter((index) => !this.acknowledged.has(index)),
      sentThisRound: [...this.sentThisRound].sort((a, b) => a - b),
      deadlineMs: this.deadlineMs,
      currentPoll: this.currentPoll ? { ...this.currentPoll } : null,
      failureCode: this.failureCode,
      complete: this.state === QR_ARQ_STATE.COMPLETE,
    };
  }

  matchesCurrentPoll(status) {
    return Boolean(this.currentPoll)
      && status.sessionId === this.currentPoll.sessionId
      && status.transferCrc === this.currentPoll.transferCrc
      && status.total === this.currentPoll.total
      && status.roundId === this.currentPoll.roundId;
  }

  onTimeout() {
    if (this.pollAttempts >= this.maxPollAttempts) {
      this.fail('QR_ARQ_STATUS_UNAVAILABLE');
      return;
    }
    // DATA already sent in this round are never repeated without an explicit
    // bitmap. A lost POLL or STATUS is repaired by the same POLL nonce only.
    this.state = QR_ARQ_STATE.POLL;
    this.deadlineMs = null;
  }

  startNextRound(pending, failureCode) {
    if (this.round >= this.maxRounds) {
      this.fail(failureCode);
      return false;
    }
    this.round += 1;
    this.queue = [...new Set(pending)].sort((a, b) => a - b);
    this.cursor = 0;
    this.sentThisRound = new Set();
    this.currentPoll = null;
    this.pollAttempts = 0;
    this.deadlineMs = null;
    this.state = QR_ARQ_STATE.SEND_MISSING;
    return true;
  }

  observeTime(nowMs) {
    if (!Number.isFinite(nowMs) || nowMs < 0) throw new RangeError('timestamp ARQ invalide');
    if (this.lastNowMs !== null && nowMs < this.lastNowMs) throw new RangeError('timestamp ARQ non monotone');
    this.lastNowMs = nowMs;
  }

  fail(code) {
    this.state = QR_ARQ_STATE.FAILED;
    this.failureCode = code;
    this.deadlineMs = null;
    return { type: 'FAILED', ...this.snapshot() };
  }
}

function validateTransfer(transfer) {
  if (!transfer || !Array.isArray(transfer.packets) || !Number.isInteger(transfer.total)
      || transfer.total < 1 || transfer.packets.length !== transfer.total) {
    throw new TypeError('transfert DATA LQP1 invalide');
  }
  assertHex32(transfer.sessionId, 'sessionId');
  assertHex32(transfer.transferCrc, 'transferCrc');
  const indices = new Set();
  for (const [position, input] of transfer.packets.entries()) {
    const packet = input?.encodedText ? parseQrPacket(input.encodedText) : { ok: false };
    if (!packet.ok || packet.type !== 'DATA' || packet.sessionId !== transfer.sessionId
        || packet.transferCrc !== transfer.transferCrc || packet.total !== transfer.total
        || packet.index !== position || indices.has(packet.index)) {
      throw new TypeError('collection DATA LQP1 incohérente');
    }
    indices.add(packet.index);
  }
  if (indices.size !== transfer.total || allIndices(transfer.total).some((index) => !indices.has(index))) {
    throw new TypeError('indices DATA LQP1 incomplets');
  }
}

function normalizeStatus(input) {
  if (typeof input === 'string') return parseQrPacket(input);
  if (input?.encodedText) return parseQrPacket(input.encodedText);
  return { ok: false, code: 'QR_PACKET_INVALID' };
}

/* ------------------------------------------------------------------ */
/*  UDP side: fire-and-forget broadcast, no STATUS expected           */
/* ------------------------------------------------------------------ */

/**
 * Fire-and-forget sender. Replays every DATA packet of the transfer
 * `repeats` times, then completes without any acknowledgment. The receiving
 * device still reassembles and delivers on its own; no return status exists.
 */
export class QrUdpSender {
  constructor(transfer, {
    repeats = 2,
  } = {}) {
    validateTransfer(transfer);
    if (!Number.isInteger(repeats) || repeats < 1 || repeats > 255) {
      throw new RangeError('repeats doit être compris entre 1 et 255');
    }
    this.transfer = transfer;
    this.repeats = repeats;
    this.state = 'SEND_PENDING';
    this.round = 0;
    this.cursor = 0;
    this.lastNowMs = null;
  }

  nextAction(nowMs) {
    this.observeTime(nowMs);
    if (this.state === 'COMPLETE') return { type: 'COMPLETE', ...this.snapshot() };

    if (this.cursor >= this.transfer.total) {
      this.cursor = 0;
      this.round += 1;
    }
    if (this.round >= this.repeats && this.cursor === 0) {
      this.state = 'COMPLETE';
      return { type: 'COMPLETE', ...this.snapshot() };
    }

    const index = this.cursor++;
    return {
      type: 'DATA',
      packet: this.transfer.packets[index],
      index,
      round: this.round + 1,
      repeats: this.repeats,
      ...this.snapshot(),
    };
  }

  snapshot() {
    return {
      state: this.state,
      round: this.round + 1,
      repeats: this.repeats,
      sessionId: this.transfer.sessionId,
      transferCrc: this.transfer.transferCrc,
      total: this.transfer.total,
      cursor: this.cursor,
      complete: this.state === 'COMPLETE',
      failureCode: null,
    };
  }

  observeTime(nowMs) {
    if (!Number.isFinite(nowMs) || nowMs < 0) throw new RangeError('timestamp ARQ invalide');
    if (this.lastNowMs !== null && nowMs < this.lastNowMs) throw new RangeError('timestamp ARQ non monotone');
    this.lastNowMs = nowMs;
  }
}

function defaultRoundId({ sessionId, transferCrc, round }) {
  return crc32c(new TextEncoder().encode(`${sessionId}|${transferCrc}|${round}`))
    .toString(16).padStart(8, '0');
}

function assertHex32(value, label) {
  if (typeof value !== 'string' || !/^[0-9a-f]{8}$/u.test(value)) {
    throw new TypeError(`${label} doit contenir 8 chiffres hexadécimaux minuscules`);
  }
}

function allIndices(total) { return Array.from({ length: total }, (_, index) => index); }
